import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import PostCard from "@/components/PostCard";
import UploadButton from "@/components/UploadButton";
import CategoryTypeahead from "@/components/CategoryTypeahead";
import AuthGate from "@/components/AuthGate";
import { ProfilePostFeedCard, ProfilePostModal } from "@/components/ProfilePost";
import { Sparkles, Newspaper, LayoutGrid, Plus, X, MapPin, ClipboardList } from "lucide-react";
import { toast, Toaster } from "sonner";

function FollowingList({ pros }) {
  if (!pros.length) return null;
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5" data-testid="feed-following-list">
      <h3 className="font-bold text-base mb-3" style={{ fontFamily: "Outfit" }}>Following</h3>
      <div className="space-y-1">
        {pros.map((f) => (
          <Link key={f.user_id} to={f.kind === "client" ? `/client/${f.user_id}` : `/pro/${f.user_id}`} className="flex items-center gap-3 py-1.5 px-2 rounded-xl hover:bg-black/5 group" data-testid={`following-${f.user_id}`}>
            <div className="relative">
              {f.avatar_url
                ? <img src={f.avatar_url} className="w-9 h-9 rounded-full object-cover" alt=""/>
                : <div className="w-9 h-9 rounded-full bg-[#2E6B57] grid place-items-center text-sm font-bold">{f.business_name?.[0]}</div>}
              {f.has_new && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#2E6B57] ring-2 ring-white" data-testid={`following-new-${f.user_id}`}/>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{f.business_name}</p>
              <p className="text-xs text-[#222]/50 truncate">{f.kind === "client" ? "Neighbor" : f.category}</p>
            </div>
          </Link>
        ))}
      </div>
      <Link to="/following" className="mt-3 inline-block text-xs text-[#245544] font-semibold" data-testid="following-see-all">See all →</Link>
    </div>
  );
}

function Suggestions({ pros, onFollow }) {
  if (!pros.length) return null;
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5" data-testid="feed-suggestions">
      <h3 className="font-bold text-base mb-1" style={{ fontFamily: "Outfit" }}>Pros worth following</h3>
      <p className="text-xs text-[#222]/50 mb-3">Pro Plus members with fresh posts.</p>
      <div className="space-y-3">
        {pros.map((s) => (
          <div key={s.user_id} className="flex items-center gap-3">
            <Link to={`/pro/${s.user_id}`} className="shrink-0">
              {s.avatar_url
                ? <img src={s.avatar_url} className="w-10 h-10 rounded-full object-cover" alt=""/>
                : <div className="w-10 h-10 rounded-full bg-[#2E6B57] grid place-items-center font-bold">{s.business_name?.[0]}</div>}
            </Link>
            <div className="flex-1 min-w-0">
              <Link to={`/pro/${s.user_id}`} className="text-sm font-semibold truncate flex items-center gap-1 hover:underline">
                {s.business_name}<Sparkles size={11} className="text-[#2E6B57]"/>
              </Link>
              <p className="text-xs text-[#222]/50 truncate">{s.category}</p>
            </div>
            <button onClick={() => onFollow(s.user_id)} className="btn-lime px-3 py-1 text-xs" data-testid={`suggestion-follow-${s.user_id}`}>Follow</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function WallView({ user, cfg }) {
  const [posts, setPosts] = useState([]);
  const [radius, setRadius] = useState(user?.last_wall_radius || 25);
  const [selectedCats, setSelectedCats] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [neighborSub] = useState("all");
  const [cats, setCats] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [needsLocation, setNeedsLocation] = useState(false);
  const [zipInput, setZipInput] = useState(user?.zip || localStorage.getItem("lima_zip") || "");
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [locLabel, setLocLabel] = useState("");
  const [coords, setCoords] = useState(null);      // {lat,lon} when using current location
  const [source, setSource] = useState("profile"); // profile | current
  const [showLoc, setShowLoc] = useState(false);
  const [anonZip, setAnonZip] = useState(localStorage.getItem("lima_zip") || "");

  const params = (p) => {
    const base = { radius, category: selectedCats.join(","), post_type: typeFilter, page: p };
    if (source === "current" && coords) { base.lat = coords.lat; base.lon = coords.lon; }
    else if (!user && anonZip) { base.zip = anonZip; }
    return base;
  };
  const load = (p = 1) => {
    setLoading(true);
    api.get("/wall", { params: params(p) })
      .then(r => {
        setNeedsLocation(!!r.data.needs_location);
        setLocLabel(r.data.location_label || "");
        setPosts(prev => p === 1 ? (r.data.posts || []) : [...prev, ...(r.data.posts || [])]);
        setHasMore(!!r.data.has_more);
        setPage(p);
      }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { api.get("/wall/categories").then(r => setCats(r.data.categories || [])).catch(() => {}); }, []);
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [radius, selectedCats, typeFilter, source, coords]);

  const saveZip = async () => {
    if (/^\d{5}$/.test((zipInput || "").trim())) localStorage.setItem("lima_zip", zipInput.trim());
    if (!user) {   // logged-out visitor: browse by ZIP without an account
      setAnonZip(zipInput); setSource("profile"); setCoords(null); setShowLoc(false); setNeedsLocation(false);
      setTimeout(() => load(1), 0);
      return;
    }
    try {
      await api.patch("/users/me", { zip: zipInput });
      setSource("profile"); setCoords(null); setShowLoc(false); setNeedsLocation(false);
      load(1); toast.success("Location saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid ZIP"); }
  };
  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error("Location not available"); return; }
    navigator.geolocation.getCurrentPosition(
      pos => { setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setSource("current"); setShowLoc(false); toast.success("Using your current location"); },
      () => toast.message("Location permission denied — keeping your profile ZIP."),
    );
  };
  const useProfileZip = () => { setSource("profile"); setCoords(null); setShowLoc(false); };
  const addCat = (c) => { if (c && !selectedCats.includes(c) && selectedCats.length < 3) setSelectedCats([...selectedCats, c]); };

  if (needsLocation) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center border border-dashed border-black/15 max-w-md mx-auto" data-testid="wall-location-prompt">
        <div className="w-14 h-14 rounded-full bg-[#E9F0EC] grid place-items-center mx-auto mb-3"><MapPin size={22} className="text-[#245544]"/></div>
        <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Set your location</h3>
        <p className="text-sm text-[#222]/60 mb-4">Add your ZIP code to see what neighbors near you are sharing and looking for.</p>
        <div className="flex gap-2 justify-center">
          <input value={zipInput} onChange={e => setZipInput(e.target.value)} maxLength={5} placeholder="ZIP code" aria-label="Wall location ZIP code"
            className="px-3 py-2 border border-black/10 rounded-full text-sm w-32 text-center outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="wall-zip-input"/>
          <button onClick={saveZip} className="btn-lime px-5 py-2 text-sm" data-testid="wall-zip-save">Save</button>
        </div>
        <button onClick={useCurrentLocation} className="mt-3 text-sm text-[#245544] font-semibold" data-testid="wall-use-current">Or use my current location</button>
      </div>
    );
  }

  const catLabel = selectedCats.length ? selectedCats.join(", ") : "all categories";
  return (
    <div data-testid="the-wall">
      <p className="text-sm text-[#222]/60 mb-2">See what people near you are sharing and looking for.</p>

      {/* Location bar */}
      <div className="text-sm text-[#222]/70 mb-3 flex items-center gap-2 flex-wrap" data-testid="wall-location-bar">
        <MapPin size={14} className="text-[#245544]"/>
        <span>{source === "current" ? "Current location" : (locLabel || (user?.zip ? `Near ${user.zip}` : "Set your location"))}</span>
        <span className="text-[#222]/30">·</span>
        <span>Within {radius} miles</span>
        <button onClick={() => setShowLoc(s => !s)} className="text-[#245544] font-semibold" data-testid="wall-change-location">Change</button>
        {showLoc && (
          <div className="w-full mt-2 bg-white border border-black/10 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="wall-location-popover">
            <input value={zipInput} onChange={e => setZipInput(e.target.value)} maxLength={5} placeholder="ZIP" aria-label="Wall location ZIP code"
              className="px-3 py-1.5 border border-black/10 rounded-full text-sm w-28 text-center"/>
            <button onClick={saveZip} className="btn-lime px-3 py-1.5 text-xs">Save ZIP</button>
            <button onClick={useCurrentLocation} className="px-3 py-1.5 rounded-full border border-black/10 text-xs" data-testid="wall-use-current">Use my current location</button>
            <button onClick={useProfileZip} className="px-3 py-1.5 rounded-full border border-black/10 text-xs" data-testid="wall-use-profile">Use profile ZIP</button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="inline-flex bg-white border border-black/10 rounded-full p-1 text-xs overflow-x-auto max-w-full" role="group" aria-label="Wall content type">
          {[["all", "All"], ["projects", "Projects"], ["recommendations", "Recommendations"], ["pro_posts", "Pro Posts"]].map(([k, label]) => (
            <button key={k} onClick={() => setTypeFilter(k)} className={`px-3 py-1 rounded-full font-semibold whitespace-nowrap ${typeFilter === k ? "bg-[#222] text-white" : "text-[#222]/60"}`} data-testid={`wall-type-${k}`}>{label}</button>
          ))}
        </div>
        <select value={radius} onChange={e => setRadius(Number(e.target.value))} aria-label="Wall radius" className="px-3 py-1.5 rounded-full border border-black/10 bg-white text-xs outline-none" data-testid="wall-radius">
          {[5, 10, 25, 50].map(r => <option key={r} value={r}>{r} mi</option>)}
        </select>
        <select value="" onChange={e => { addCat(e.target.value); e.target.value = ""; }} aria-label="Category or service" disabled={selectedCats.length >= 3}
          className="px-3 py-1.5 rounded-full border border-black/10 bg-white text-xs outline-none max-w-[170px]" data-testid="wall-category">
          <option value="">+ Category ({selectedCats.length}/3)</option>
          {cats.filter(c => !selectedCats.includes(c)).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={() => setShowCompose(true)} className="ml-auto btn-lime px-4 py-2 text-sm flex items-center gap-1.5" data-testid="wall-create-post-btn"><Plus size={15}/> Create post</button>
      </div>

      {/* Active category chips */}
      {selectedCats.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="wall-active-cats">
          <span className="text-xs text-[#222]/50">Matching any:</span>
          {selectedCats.map(c => (
            <span key={c} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#2E6B57]/20 text-[#245544] text-xs font-semibold">
              {c}<button onClick={() => setSelectedCats(selectedCats.filter(x => x !== c))} data-testid={`wall-cat-remove-${c}`}><X size={12}/></button>
            </span>
          ))}
          <button onClick={() => setSelectedCats([])} className="text-xs text-[#222]/50 underline ml-1" data-testid="wall-clear-cats">Clear</button>
        </div>
      )}

      {loading && posts.length === 0 ? (
        <div className="text-center py-16 text-[#222]/40">Loading the Wall…</div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center border border-dashed border-black/15" data-testid="wall-empty">
          <p className="text-[#222]/70 font-semibold mb-1">Nothing here yet</p>
          <p className="text-sm text-[#222]/50">No {typeFilter === "pros" ? "pro posts" : typeFilter === "neighbors" ? "neighbor posts" : "posts"} within {radius} miles of {source === "current" ? "your current location" : (user?.zip || "your area")}{selectedCats.length ? ` matching ${catLabel}` : ""}. Try a wider radius or clearing filters.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {posts.map(p => (
            <PostCard key={p.id} post={p} author={p.author}
              theme={p.author?.card_color ? { card: p.author.card_color, pin: p.author.pin_color } : { card: "#fff", pin: "#C79B6B" }}
              reactions={cfg.post_reactions} chips={cfg.comment_chips}
              context="wall" onChanged={() => load(1)} onImageClick={() => setLightbox(p)} />
          ))}
          {hasMore && (
            <button onClick={() => load(page + 1)} className="w-full py-3 rounded-xl bg-white border border-black/10 text-sm font-semibold hover:bg-black/5" data-testid="wall-load-more">Load more</button>
          )}
        </div>
      )}

      {showCompose && <WallComposer user={user} onClose={() => setShowCompose(false)} onPosted={() => { setShowCompose(false); load(1); }} />}

      {lightbox && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-4" onClick={() => setLightbox(null)} data-testid="wall-lightbox">
          <img src={lightbox.image_url} className="max-w-full max-h-[90vh] rounded-xl object-contain" alt="" onClick={e => e.stopPropagation()}/>
        </div>
      )}
    </div>
  );
}

function WallComposer({ user, onClose, onPosted }) {
  const isPro = user?.role === "pro";
  const [type, setType] = useState(isPro ? "pro_post" : "project");
  const [form, setForm] = useState({ content: "", image_url: "", category: "", zip: user?.zip || "", tagged_pro_id: "" });
  const [taggable, setTaggable] = useState([]);
  const [busy, setBusy] = useState(false);
  const [elig, setElig] = useState(null);
  const contentRef = useRef(null);
  const [mention, setMention] = useState(null);      // { token, start } while typing "@…"
  const [mentionHits, setMentionHits] = useState([]);
  const [wideHits, setWideHits] = useState(null);    // null = wider search not run yet
  const [wideBusy, setWideBusy] = useState(false);

  useEffect(() => {
    if (!isPro) api.get("/wall/taggable-pros").then(r => setTaggable(r.data.pros || [])).catch(() => setTaggable([]));
    api.get("/wall/eligibility").then(r => setElig(r.data)).catch(() => {});
  }, [isPro]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
  const eligText = () => {
    if (!elig) return "";
    if (elig.allowed) return `Posting to: Wall + your profile · ${elig.remaining} of ${elig.cap} Wall post${elig.cap === 1 ? "" : "s"} available.`;
    if (!isPro) return elig.message;  // clients: hard limit, nothing gets posted
    return `This will post to your profile & followers' Feed only. ${elig.message} Next Wall slot: ${fmt(elig.next_slot)}.`;
  };
  const blocked = !isPro && elig && !elig.allowed;

  const pickPro = async (p) => {
    setForm(f => ({ ...f, tagged_pro_id: p.user_id }));
    if (!p.on_board) {
      try { await api.post(`/boards/pin/${p.user_id}`); toast.success(`${p.business_name} saved to My Board`); }
      catch { /* already pinned / ignore */ }
    }
    setTaggable(list => list.some(x => x.user_id === p.user_id) ? list : [...list, { ...p, on_board: true }]);
  };

  /** Inline "@" Pro picker — My Board only. If the pro isn't saved there yet,
   *  "Can't find them? Find a Pro" searches all of LIMYA and saving happens on
   *  selection. Draft text is never touched. */
  const onContentChange = (e) => {
    const value = e.target.value;
    setForm(f => ({ ...f, content: value }));
    if (type !== "recommendation") return;
    const caret = e.target.selectionStart ?? value.length;
    const m = /@([^@\s]{0,30})$/.exec(value.slice(0, caret));
    if (!m) { setMention(null); setMentionHits([]); setWideHits(null); return; }
    const token = m[1];
    setMention({ token, start: caret - m[0].length });
    setWideHits(null);
    setMentionHits(
      taggable
        .filter(p => p.on_board)
        .filter(p => !token || (p.business_name || "").toLowerCase().includes(token.toLowerCase()))
        .slice(0, 6)
    );
  };

  const findAnyPro = async () => {
    if (!mention) return;
    setWideBusy(true);
    try {
      const r = await api.get("/wall/taggable-pros", { params: mention.token ? { q: mention.token } : {} });
      const onBoard = new Set(taggable.filter(p => p.on_board).map(p => p.user_id));
      setWideHits((r.data.pros || []).filter(p => !onBoard.has(p.user_id)).slice(0, 10));
    } catch { setWideHits([]); }
    finally { setWideBusy(false); }
  };

  const applyMention = async (p) => {
    if (mention) {
      const { start, token } = mention;
      setForm(f => ({
        ...f,
        content: `${f.content.slice(0, start)}@${p.business_name} ${f.content.slice(start + token.length + 1)}`,
      }));
    }
    setMention(null); setMentionHits([]); setWideHits(null);
    await pickPro(p);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/wall", { post_type: type, ...form });
      toast[r.data.on_wall ? "success" : "message"](r.data.message);
      onPosted();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post"); }
    finally { setBusy(false); }
  };

  const limitNote = isPro
    ? ((elig?.is_pro_plus ?? user?.pro_plus_access) ? "Pro Plus: up to 2 Wall posts / 24h, at least 6h apart." : "Free Pro: 1 Wall post / 24h. Extra posts still go to your profile & followers' Feed.")
    : "Clients: 1 Wall post / 24h — shared by Projects and Recommendations. Requests don't count toward it.";

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="wall-composer">
        <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>{isPro ? "Post to the Wall" : "Create a post"}</h3>
        {!isPro && (
          <>
            <p className="text-xs text-[#222]/55 mt-1 mb-3" data-testid="compose-client-note">
              The Wall is for sharing projects and recommending pros. Asking for help?{" "}
              <Link to="/requests" className="text-[#245544] font-semibold underline">Create a Request</Link> instead — Requests don't use your Wall post.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-4" data-testid="compose-type-picker">
              {[
                { key: "project", label: "Project", hint: "Share something you had done" },
                { key: "recommendation", label: "Recommendation", hint: "Recommend a pro you used" },
              ].map(t => (
                <button key={t.key} onClick={() => setType(t.key)}
                  className={`text-left rounded-xl border px-3 py-2.5 transition ${
                    type === t.key ? "border-[#2E6B57] bg-[#E9F0EC]" : "border-black/10 hover:bg-black/[0.03]"}`}
                  aria-pressed={type === t.key}
                  data-testid={`compose-type-${t.key}`}>
                  <p className={`font-bold text-sm ${type === t.key ? "text-[#245544]" : ""}`} style={{ fontFamily: "Outfit" }}>{t.label}</p>
                  <p className="text-[11px] text-[#222]/55 mt-0.5">{t.hint}</p>
                </button>
              ))}
            </div>
          </>
        )}

        {type === "project" && (
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Category (optional)</label>
              <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                placeholder="e.g. Landscaping"
                className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm" data-testid="project-category-input"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Credit a Pro (optional)</label>
              <select value={form.tagged_pro_id} onChange={e => setForm({ ...form, tagged_pro_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="project-pro-select">
                <option value="">No credit</option>
                {taggable.map(p => <option key={p.user_id} value={p.user_id}>{p.business_name}{p.worked_together ? " · worked together" : p.on_board ? " · on my board" : p.following ? " · following" : ""}</option>)}
              </select>
            </div>
          </div>
        )}

        {type === "recommendation" && (
          form.tagged_pro_id ? (() => {
            const sel = taggable.find(p => p.user_id === form.tagged_pro_id);
            return (
              <div className="mb-3 flex items-center justify-between bg-[#E9F0EC] rounded-xl px-3 py-2 text-sm" data-testid="rec-selected-pro">
                <span className="font-semibold text-[#245544] truncate">Tagging {sel?.business_name || "a pro"}</span>
                <button onClick={() => setForm(f => ({ ...f, tagged_pro_id: "" }))} className="text-xs text-[#222]/50 shrink-0" data-testid="rec-clear-pro">Remove tag</button>
              </div>
            );
          })() : (
            <p className="text-xs text-[#222]/50 mb-3" data-testid="rec-tag-hint">Type <span className="font-semibold">@</span> in your recommendation to tag the pro — pick from My Board, or use Find a Pro to search all of LIMYA.</p>
          )
        )}

        <div className="relative">
          <textarea ref={contentRef} rows={3} value={form.content} onChange={onContentChange}
            placeholder={type === "recommendation" ? "Why do you recommend them? Type @ to tag a pro…"
                        : type === "project" ? "What did you have done? Add a photo and a short caption…"
                        : "Share an update, project, or availability…"}
            className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="compose-content"/>
          {mention && (
            <div className="absolute left-0 right-0 z-10 mt-1 bg-white border border-black/10 rounded-xl shadow-lg overflow-hidden max-h-60 overflow-y-auto" data-testid="mention-picker">
              <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#222]/40 font-semibold">My Board</p>
              {mentionHits.length === 0 ? (
                <p className="px-3 pb-2 text-xs text-[#222]/45">No saved pros match “{mention.token}”.</p>
              ) : mentionHits.map(p => (
                <button key={p.user_id} onClick={() => applyMention(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-black/5 flex items-center justify-between gap-2" data-testid={`mention-opt-${p.user_id}`}>
                  <span className="truncate">{p.business_name} <span className="text-[#222]/40 text-xs">{p.category}</span></span>
                  <span className="text-[10px] text-[#245544] font-semibold shrink-0">On my board</span>
                </button>
              ))}
              {wideHits === null ? (
                <button onClick={findAnyPro} disabled={wideBusy} className="w-full text-left px-3 py-2.5 text-xs font-semibold text-[#245544] border-t border-black/5 hover:bg-black/5 disabled:opacity-50" data-testid="mention-find-a-pro">
                  {wideBusy ? "Searching all of LIMYA…" : "Can't find them? Find a Pro"}
                </button>
              ) : (
                <>
                  <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#222]/40 font-semibold border-t border-black/5">All LIMYA Pros</p>
                  {wideHits.length === 0 ? (
                    <p className="px-3 pb-2 text-xs text-[#222]/45">No pros found. Try a different name.</p>
                  ) : wideHits.map(p => (
                    <button key={p.user_id} onClick={() => applyMention(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-black/5 flex items-center justify-between gap-2" data-testid={`mention-wide-opt-${p.user_id}`}>
                      <span className="truncate">{p.business_name} <span className="text-[#222]/40 text-xs">{p.category}</span></span>
                      <span className="text-[10px] text-[#222]/45 font-semibold shrink-0">Save to My Board</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        <p className="text-xs text-[#222]/45 mt-1">For your safety, please don't post phone numbers or exact addresses. Use LIMYA messages to connect privately.</p>

        {form.image_url && <img src={form.image_url} className="h-24 rounded-lg object-cover mt-2" alt=""/>}

        <p className="text-[11px] text-[#222]/45 mt-3">{limitNote}</p>
        {elig && (
          <div className={`mt-2 text-xs rounded-lg px-3 py-2 ${elig.allowed ? "bg-[#E9F0EC] text-[#245544]" : blocked ? "bg-red-50 text-red-700" : "bg-[#FEF3C7] text-[#92400E]"}`} data-testid="compose-eligibility">
            {eligText()}
          </div>
        )}
        <div className="flex items-center justify-between mt-3">
          <UploadButton onUploaded={(url) => setForm(f => ({ ...f, image_url: url }))} label="Add photo"/>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm" data-testid="compose-cancel">Cancel</button>
            <button onClick={submit} disabled={busy || blocked} className="btn-lime px-5 py-2 text-sm disabled:opacity-50" data-testid="compose-submit" title={blocked ? elig.message : ""}>Post</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const HUB_NAV = {
  pro: [
    { key: "requests", path: "/requests", label: "Requests", icon: ClipboardList },
    { key: "feed", path: "/feed", label: "Feed", icon: Newspaper },
    { key: "wall", path: "/wall", label: "Wall", icon: LayoutGrid },
  ],
  client: [
    { key: "feed", path: "/feed", label: "Feed", icon: Newspaper },
    { key: "wall", path: "/wall", label: "Wall", icon: LayoutGrid },
    { key: "requests", path: "/requests", label: "My Requests", icon: ClipboardList },
  ],
};

const HUB_DESC = {
  pro: {
    requests: ["Requests", "Find local customers looking for what you do."],
    feed: ["Your Feed", "See recent activity from people and Pros you follow."],
    wall: ["The Wall", "Explore projects, recommendations and posts from local Pros."],
  },
  client: {
    feed: ["Your Feed", "See recent activity from Pros and connections you follow."],
    wall: ["The Wall", "Explore projects, recommendations and posts from local Pros."],
    requests: ["My Requests", "Create a request or manage requests you've already made."],
  },
};

const REQ_STATUS = {
  open: ["Open", "#245544", "#E9F0EC"],
  filled: ["Filled", "#7a6a1f", "#FBF3D6"],
  closed: ["Closed", "#6b6b6b", "#eeeeee"],
  expired: ["Expired", "#8a5a30", "#F3E7DC"],
};

function CreateRequestModal({ user, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", category: "", details: "", zip: user?.zip || "", image_urls: [] });
  const [catText, setCatText] = useState("");
  const [coords, setCoords] = useState(null);
  const [locBusy, setLocBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error("Your browser can't share a location"); return; }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setLocBusy(false); toast.success("Using your current location"); },
      () => { setLocBusy(false); toast.error("Couldn't get your location — add a ZIP instead"); },
      { timeout: 8000 },
    );
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Add a short title"); return; }
    if (!form.category) { toast.error("Choose a category so the right pros see your request"); return; }
    if (!form.zip.trim() && !coords) { toast.error("Add a ZIP or use your current location so nearby pros can find your request"); return; }
    setBusy(true);
    try {
      await api.post("/requests", { ...form, lat: coords?.lat, lon: coords?.lon });
      toast.success("Request posted — relevant local Pros can now respond.");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create request"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-request-modal">
        <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Create a request</h3>
        <p className="text-sm text-[#222]/55 mb-4">Relevant local Pros may respond to your request through LIMYA.</p>
        <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">What do you need?</label>
        <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Repair a leaking water heater" className="w-full mt-1 mb-3 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="create-request-title"/>

        <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Category <span className="text-[#B4442E]">*</span></label>
        <div className="mt-1 mb-1">
          <CategoryTypeahead
            value={catText}
            onChange={(v) => { setCatText(v); setForm(f => ({ ...f, category: "" })); }}
            onSelect={(name) => { setCatText(name); setForm(f => ({ ...f, category: name })); }}
            placeholder="Search categories, e.g. Plumbing"
            testId="create-request-category"
            inputClassName="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
          />
        </div>
        <div className="flex items-center gap-2 mb-3">
          {form.category
            ? <span className="text-[11px] font-semibold text-[#245544] bg-[#E9F0EC] px-2 py-0.5 rounded-full" data-testid="create-request-category-selected">{form.category}</span>
            : <span className="text-[11px] text-[#222]/45">Pick from the list so the right pros are matched</span>}
          <button type="button" onClick={() => { setCatText("Other"); setForm(f => ({ ...f, category: "Other" })); }}
            className="text-[11px] font-semibold text-[#245544] underline ml-auto" data-testid="create-request-category-other">
            Not listed? Use Other
          </button>
        </div>

        <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Location</label>
        <div className="flex gap-2 mt-1">
          <input value={form.zip} onChange={e => { setForm({ ...form, zip: e.target.value }); setCoords(null); }} maxLength={5} placeholder="ZIP" className="w-28 px-3 py-2 border border-black/10 rounded-xl text-sm" data-testid="create-request-zip"/>
          <button type="button" onClick={useCurrentLocation} disabled={locBusy}
            className="px-3 py-2 text-xs font-semibold rounded-xl border border-black/10 hover:bg-black/[0.03] disabled:opacity-50 flex items-center gap-1.5"
            data-testid="create-request-use-location">
            <MapPin size={13}/> {locBusy ? "Locating…" : "Use my current location"}
          </button>
        </div>
        <p className="text-[11px] text-[#222]/45 mt-1 mb-3" data-testid="create-request-location-note">
          {coords ? "Using your current location. Pros only ever see an approximate distance."
            : form.zip.trim() ? (user?.zip && form.zip.trim() === user.zip ? "Using your profile ZIP — change it just for this request if you like." : "Using the ZIP you entered for this request.")
            : "Add a ZIP or use your current location. Pros only ever see an approximate distance."}
        </p>

        <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Details</label>
        <textarea rows={3} value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} placeholder="Add a short description…" className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="create-request-details"/>
        <p className="text-xs text-[#222]/45 mt-1">Please don't include phone numbers or your exact address — connect privately through LIMYA messages.</p>
        {form.image_urls[0] && <img src={form.image_urls[0]} className="h-24 rounded-lg object-cover mt-2" alt=""/>}
        <div className="flex items-center justify-between mt-4">
          <UploadButton onUploaded={(url) => setForm(f => ({ ...f, image_urls: [url] }))} label="Add photo"/>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm" data-testid="create-request-cancel">Cancel</button>
            <button onClick={submit} disabled={busy} className="btn-lime px-5 py-2 text-sm disabled:opacity-50" data-testid="create-request-submit">Post request</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RequestsView({ user, navigate }) {
  const isPro = user?.role === "pro";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [radius, setRadius] = useState(25);
  const [category, setCategory] = useState("");        // "" = All categories
  const [catText, setCatText] = useState("");
  const [sort, setSort] = useState("newest");
  const filtersActive = !!category || radius !== 25 || sort !== "newest";
  const [needsLoc, setNeedsLoc] = useState(false);

  const load = () => {
    setLoading(true);
    const req = isPro
      ? api.get("/requests/marketplace", { params: { radius, sort, ...(category ? { category } : {}) } })
      : api.get("/requests/mine");
    req.then(r => { setNeedsLoc(!!r.data.needs_location); setItems(r.data.requests || []); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, radius, category, sort]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }) : "";

  if (!user) return <div className="bg-white rounded-2xl p-8 text-center border border-dashed border-black/15">Please sign in.</div>;

  return (
    <div className="max-w-3xl mx-auto" data-testid="requests-view">
      {!isPro && (
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-[#222]/50">{items.length} request{items.length === 1 ? "" : "s"}</span>
          <button onClick={() => setShowCreate(true)} className="btn-lime px-4 py-2 text-sm flex items-center gap-1.5" data-testid="create-request-btn"><Plus size={15}/> Create Request</button>
        </div>
      )}
      {isPro && (
        <div className="bg-white rounded-2xl p-4 mb-4 border border-black/5 shadow-sm" data-testid="requests-filter-bar">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0">
              <label className="text-[11px] uppercase tracking-wider text-[#222]/50 font-semibold">Category</label>
              {category ? (
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#245544] bg-[#E9F0EC] px-3 py-1.5 rounded-full" data-testid="requests-category-active">{category}</span>
                  <button onClick={() => { setCategory(""); setCatText(""); }} className="text-xs text-[#222]/55 hover:underline" data-testid="requests-category-clear">Clear</button>
                </div>
              ) : (
                <div className="mt-1">
                  <CategoryTypeahead
                    value={catText}
                    onChange={setCatText}
                    onSelect={(name) => { setCatText(name); setCategory(name); }}
                    placeholder="All categories — search to filter"
                    testId="requests-category-filter"
                    inputClassName="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#222]/50 font-semibold">Within</label>
              <select value={radius} onChange={e => setRadius(Number(e.target.value))} className="block mt-1 px-3 py-2 rounded-xl border border-black/10 bg-white text-sm" data-testid="requests-radius">
                {[5, 10, 25, 50].map(r => <option key={r} value={r}>{r} miles</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#222]/50 font-semibold">Sort</label>
              <select value={sort} onChange={e => setSort(e.target.value)} className="block mt-1 px-3 py-2 rounded-xl border border-black/10 bg-white text-sm" data-testid="requests-sort">
                <option value="newest">Newest</option>
                <option value="nearest">Nearest</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className="text-[11px] text-[#222]/45">Open requests only — filled, closed and expired requests leave the marketplace automatically.</span>
            {!category && (
              <button onClick={() => { setCatText("Other"); setCategory("Other"); }} className="text-[11px] font-semibold text-[#245544] hover:underline ml-auto" data-testid="requests-category-other">
                Show "Other" requests
              </button>
            )}
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-center py-16 text-[#222]/40">Loading…</div>
      ) : needsLoc ? (
        <div className="bg-white rounded-2xl p-8 text-center border border-dashed border-black/15" data-testid="requests-needs-loc">
          <p className="text-sm text-[#222]/60">Add your business ZIP in your profile to see nearby requests.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-dashed border-black/15" data-testid="requests-empty">
          {isPro ? (
            <>
              <p className="font-semibold text-[#222]/70 mb-1">
                {filtersActive ? "No requests match these filters right now." : "No open requests nearby right now."}
              </p>
              <p className="text-sm text-[#222]/50">
                {filtersActive
                  ? "Try another category or expand your search radius."
                  : "Check back soon or expand your search radius."}
              </p>
            </>
          ) : (
            <>
              <p className="font-semibold text-[#222]/70 mb-1">Need a local Pro?</p>
              <p className="text-sm text-[#222]/50 mb-5">Create a request and let relevant local Professionals respond through LIMYA.</p>
              <button onClick={() => setShowCreate(true)} className="btn-lime px-5 py-2 text-sm inline-flex items-center gap-1.5" data-testid="create-request-empty"><Plus size={15}/> Create Request</button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(r => {
            const st = REQ_STATUS[r.status] || REQ_STATUS.open;
            return (
              <div key={r.id} onClick={() => navigate(`/requests/${r.id}`)} className="bg-white rounded-2xl border border-black/5 p-4 flex gap-4 cursor-pointer hover:border-black/15 transition" data-testid={`request-card-${r.id}`}>
                {r.image_url && <img src={r.image_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0"/>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-[#222] truncate" style={{ fontFamily: "Outfit" }}>{r.title}</h3>
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ color: st[1], background: st[2] }}>{st[0]}</span>
                  </div>
                  <p className="text-xs text-[#222]/50 mt-0.5">{[r.category, isPro && r.approx_distance ? `${r.approx_distance} mi away` : null, `Posted ${fmtDate(r.created_at)}`].filter(Boolean).join(" · ")}</p>
                  {r.details && <p className="text-sm text-[#222]/70 mt-1 line-clamp-2">{r.details}</p>}
                  <div className="mt-2 flex items-center gap-3">
                    {isPro ? (
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/messages?to=${r.client.user_id}&request=${r.id}`); }} className="text-sm font-semibold text-[#245544]" data-testid={`request-message-${r.id}`}>Message Client →</button>
                    ) : (
                      <span className="text-xs text-[#222]/50" data-testid={`request-responses-${r.id}`}>{r.response_count || 0} response{r.response_count === 1 ? "" : "s"}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {showCreate && <CreateRequestModal user={user} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }}/>}
    </div>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [following, setFollowing] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [cfg, setCfg] = useState({ post_reactions: [], comment_chips: { praise: [], intent: [] } });
  const [loading, setLoading] = useState(true);
  const [wallPost, setWallPost] = useState(null);
  const [openProfilePost, setOpenProfilePost] = useState(null);

  const refreshWallPost = async () => {
    const r = await api.get("/feed");
    setPosts(r.data.posts || []);
    setWallPost(prev => prev ? (r.data.posts || []).find(x => x.id === prev.id) || null : null);
  };

  const loadPosts = () => api.get("/feed").then(r => setPosts(r.data.posts || [])).catch(() => {});
  const loadRails = () => {
    api.get("/feed/following").then(r => setFollowing(r.data.pros || [])).catch(() => {});
    api.get("/feed/suggestions").then(r => setSuggestions(r.data.pros || [])).catch(() => {});
  };

  useEffect(() => {
    api.get("/config/public").then(r => setCfg({
      post_reactions: r.data.post_reactions || [],
      comment_chips: r.data.comment_chips || { praise: [], intent: [] },
    })).catch(() => {}).finally(() => setLoading(false));
    if (user) {
      loadPosts();
      loadRails();
      api.post("/feed/seen").catch(() => {});
    }
    // eslint-disable-next-line
  }, [user]);

  // URL is the single source of truth for which surface is shown — an
  // explicit /feed or /feed?tab=wall always wins over any prior in-app tab
  // click. Anonymous visitors have no personalized Feed, so they still fall
  // back to the public Wall by default.
  const role = user?.role === "pro" ? "pro" : "client";
  const navItems = HUB_NAV[role];
  const pathSection = location.pathname === "/requests" ? "requests"
    : location.pathname === "/wall" ? "wall" : "feed";
  const section = pathSection;

  // Feed, Wall and Requests are all member surfaces: signed-out visitors get a
  // clean gate instead of a half-usable page whose data calls 401. Browse Pros
  // and public Pro profiles stay public.
  if (!user) {
    if (user === null) return null;   // auth still resolving — no gate flash
    const gate = pathSection === "requests"
      ? { title: "Requests are for LIMYA members",
          line: "Sign in to see what neighbors near you need help with, or join free in about a minute.",
          testId: "requests-auth-gate" }
      : pathSection === "wall"
      ? { title: "The Wall is for LIMYA members",
          line: "Sign in to see projects, recommendations and posts from Pros near you, or join free in about a minute.",
          testId: "wall-auth-gate" }
      : { title: "Your Feed is for LIMYA members",
          line: "Sign in to see activity from the Pros you follow, or join free in about a minute.",
          testId: "feed-auth-gate" };
    return <AuthGate {...gate}/>;
  }
  const follow = async (id) => {
    try {
      await api.post(`/users/follow/${id}`);
      toast.success("Following!");
      setSuggestions(s => s.filter(x => x.user_id !== id));
      loadPosts(); loadRails();
    } catch { toast.error("Could not follow"); }
  };

  const themeFor = (p) => p.kind === "client_project"
    ? { card: "#fff", pin: "#C79B6B" }
    : { card: p.author?.card_color, pin: p.author?.pin_color };

  return (
    <div>
      <Header/>
      <Toaster position="top-center"/>
      <section className="px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Role-based primary navigation */}
          <div className="inline-flex bg-white border border-black/10 rounded-full p-1 mb-4 max-w-full overflow-x-auto" role="tablist" aria-label="Sections" data-testid="hub-nav">
            {navItems.map(it => {
              const Icon = it.icon;
              const active = section === it.key;
              return (
                <Link key={it.key} to={it.path}
                  className={`px-4 sm:px-5 py-1.5 rounded-full text-sm font-semibold transition flex items-center gap-1.5 whitespace-nowrap ${active ? "bg-[#222] text-white" : "text-[#222]/60 hover:text-[#222]"}`}
                  data-testid={`hub-nav-${it.key}`} aria-current={active ? "page" : undefined}>
                  <Icon size={15}/> {it.label}
                </Link>
              );
            })}
          </div>
          {/* Changing title + one short supporting line */}
          <div className="mb-6" data-testid="hub-heading">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{HUB_DESC[role][section][0]}</h1>
            <p className="text-sm text-[#222]/60 mt-1">{HUB_DESC[role][section][1]}</p>
          </div>

          {section === "requests" ? (
            <RequestsView user={user} navigate={navigate}/>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_300px] gap-6">
            <aside className="hidden lg:block lg:sticky lg:top-20 lg:self-start">
              <FollowingList pros={following}/>
            </aside>

            <div className="max-w-2xl w-full mx-auto">
              {loading ? (
                <div className="text-center py-16 text-[#222]/40">Loading…</div>
              ) : section === "wall" ? (
                <WallView user={user} cfg={cfg}/>
              ) : posts.length === 0 ? (
                <div className="bg-white rounded-2xl p-10 text-center border border-dashed border-black/15" data-testid="feed-empty">
                  <div className="w-16 h-16 rounded-full bg-[#E9F0EC] grid place-items-center mx-auto mb-4">
                    <Newspaper size={26} className="text-[#245544]"/>
                  </div>
                  <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Your feed is quiet… for now</h3>
                  <p className="text-sm text-[#222]/60 mb-6">Follow neighbors and pros — their new posts show up right here. Saving a pro to My Board doesn't add them here.</p>
                  {suggestions.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-xl mx-auto">
                      {suggestions.map(s => (
                        <div key={s.user_id} className="border border-black/5 rounded-xl p-3 text-center">
                          {s.avatar_url
                            ? <img src={s.avatar_url} className="w-14 h-14 rounded-full object-cover mx-auto" alt=""/>
                            : <div className="w-14 h-14 rounded-full bg-[#2E6B57] grid place-items-center font-bold mx-auto">{s.business_name?.[0]}</div>}
                          <p className="text-sm font-semibold truncate mt-2">{s.business_name}</p>
                          <p className="text-xs text-[#222]/50 truncate mb-2">{s.category}</p>
                          <button onClick={() => follow(s.user_id)} className="btn-lime w-full py-1.5 text-xs" data-testid={`empty-follow-${s.user_id}`}>Follow</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Link to="/search" className="inline-block mt-6 text-sm text-[#245544] font-semibold">Browse all pros →</Link>
                </div>
              ) : (
                <div className="space-y-5">
                  {posts.map(p => p.item_type === "profile_post" ? (
                    <ProfilePostFeedCard key={p.id} post={p} onOpen={() => setOpenProfilePost(p.id)}/>
                  ) : (
                    <PostCard
                      key={p.id} post={p} author={p.author}
                      theme={themeFor(p)}
                      reactions={cfg.post_reactions} chips={cfg.comment_chips}
                      onChanged={loadPosts}
                    />
                  ))}
                </div>
              )}
            </div>

            <aside className="hidden lg:block lg:sticky lg:top-20 lg:self-start">
              <Suggestions pros={suggestions} onFollow={follow}/>
            </aside>
          </div>
          )}
        </div>
      </section>

      {/* Wall lightbox — open a photo to react & ask questions */}
      {wallPost && (
        <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4 sm:p-8" onClick={() => setWallPost(null)}>
          <div className="max-w-lg mx-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button onClick={() => setWallPost(null)} className="w-9 h-9 rounded-full bg-white/90 grid place-items-center" data-testid="wall-lightbox-close"><X size={18}/></button>
            </div>
            <PostCard
              post={wallPost}
              author={wallPost.author}
              theme={themeFor(wallPost)}
              reactions={cfg.post_reactions}
              chips={cfg.comment_chips}
              onChanged={refreshWallPost}
            />
          </div>
        </div>
      )}
      {openProfilePost && (
        <ProfilePostModal postId={openProfilePost} onClose={() => setOpenProfilePost(null)} onChanged={loadPosts}/>
      )}
    </div>
  );
}
